import { cn } from "@/lib/utils";
import { shouldReloadForStaleChunk } from "@/lib/chunk-load-recovery";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  reloading: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, reloading: false };
  }

  static getDerivedStateFromError(): Pick<State, "hasError"> {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    try {
      if (shouldReloadForStaleChunk(error, sessionStorage)) {
        this.setState({ reloading: true });
        window.location.reload();
      }
    } catch {
      // sessionStorage can throw in locked-down browsers
    }
  }

  render() {
    if (this.state.reloading) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-8">
          <p className="text-sm text-muted-foreground">جاري التحميل...</p>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-8">
          <div className="flex w-full max-w-md flex-col items-center p-8 text-center">
            <AlertTriangle
              size={48}
              className="mb-6 flex-shrink-0 text-destructive"
            />

            <h2 className="mb-2 text-xl font-semibold">تعذر تحميل الصفحة</h2>
            <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
              حدث خطأ غير متوقع. أعد تحميل الصفحة للمحاولة مرة أخرى.
            </p>

            <button
              type="button"
              onClick={() => window.location.reload()}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2",
                "bg-primary text-primary-foreground",
                "hover:opacity-90",
              )}
            >
              <RotateCcw size={16} />
              إعادة تحميل الصفحة
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
